import React from 'react';

import type { ReactNode } from 'react';

import { PROMOTION_CLASS } from '@travel/traveler-core/constants/salesPromotion';
import { Tag } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import PlanPaymentRestriction from 'components/PlanPaymentRestriction';

import { Translate } from 'core/translate';
import { usePrice } from 'hooks/usePrice';
import type { Price } from 'ProviderList-Types';
import type { PlanPaymentRestrictionFeature } from 'ProviderRatePlan-Types';

import DirectPromotionBadge from './DirectPromotionBadge';

import styles from './priceLabel.module.scss';

type Props = {
  /** Custom style for wrapper */
  className?: string;
  /** Price information */
  price?: Price;
  /** Object of Plan payment restriction  */
  planPaymentRestrictionFeature?: PlanPaymentRestrictionFeature;
  /** Search condition for number of guest */
  guestsLabel?: ReactNode;
  /** Search condition for number of rooms */
  roomsLabel?: ReactNode;
  /** Search condition for number of stays */
  nightsLabel?: ReactNode;
  /** String to define charge unit */
  chargeUnit?: string | ReactNode;
  /** Component to render the price */
  PriceComponent: React.ComponentType<{ value: number }>;
  /** Label text for tax information */
  taxesLabel?: ReactNode;
  /** Flag to define whether the promotion label on PriceLabel will be hidden or not */
  isPromotionLabelHidden?: boolean;
  /** Flag to define whether the plan payment will be displayed or not */
  isShowPlanPaymentRestriction?: boolean;
  /** Flag to define whether the promotion popup will be displayed on click or not */
  hasTotalPriceSuffix?: boolean;
  /** Flag to define the discountRate styles */
  isRowDiscountRateTag?: boolean;
  /** Flag for the direct contract type */
  isDirectContract: boolean;
  /** Flag to display total price */
  isDisplayTotalPrice?: boolean;
  /** Flag to display price in map view */
  isMapView?: boolean;
};

function PriceLabel(props: Props) {
  if (!props.price) {
    return null;
  }

  const {
    PriceComponent,
    price,
    isRowDiscountRateTag = true,
    isDirectContract,
    hasTotalPriceSuffix,
    isDisplayTotalPrice = true,
    isMapView,
  } = props;

  // do not need to show sub sales promotion representative name if it has same name with main sales promotion representative name
  const mainSalesPromotion = price.salesPromotions?.find(
    (sp) => sp.promotionClass === PROMOTION_CLASS.MAIN,
  );
  const subSalesPromotion = price.salesPromotions?.find(
    (sp) => sp.promotionClass === PROMOTION_CLASS.SUB,
  );
  const isSameSalesPromotionRepresentativeName =
    mainSalesPromotion &&
    subSalesPromotion &&
    mainSalesPromotion.representativeFeatureName === subSalesPromotion.representativeFeatureName;

  const showTotalPriceSuffix = hasTotalPriceSuffix && isDirectContract;

  const { unitPrice, basePrice, totalPrice, priceWithoutDiscount, isConsumptionTaxIncluded } =
    usePrice(price, isDirectContract);

  const noOfItemsPerGuest = () => {
    return (
      Boolean(props.guestsLabel && props.nightsLabel) && (
        <div className={styles.label} data-testid="priceLabel-guestsNights-label">
          {props.guestsLabel} <>{props.nightsLabel}&nbsp;</>
          {props.roomsLabel && <>{props.roomsLabel}</>}
        </div>
      )
    );
  };

  return (
    <>
      <div className={cx(props.className, styles.priceLabel)} data-testid="priceLabel-wrapper">
        {isNotEmptyArray(price.salesPromotions) && !props.isPromotionLabelHidden && (
          <div className={styles.badgeWrapper}>
            {mainSalesPromotion && (
              <DirectPromotionBadge
                key="main"
                className={styles.direct}
                salesPromotion={mainSalesPromotion}
                planPaymentRestrictionFeature={props.planPaymentRestrictionFeature}
              />
            )}
            {subSalesPromotion && !isSameSalesPromotionRepresentativeName && (
              <DirectPromotionBadge
                key="sub"
                className={styles.direct}
                salesPromotion={subSalesPromotion}
              />
            )}
          </div>
        )}
        <div className={styles.priceWrapper}>
          <div className={styles.totalPriceWrapper}>
            <span className={cx(styles.price)} data-testid="priceLabel-unitPrice">
              {Boolean(props.chargeUnit) && isMapView && (
                <span className={styles.label}>{props.chargeUnit}</span>
              )}
              <PriceComponent value={isDirectContract ? totalPrice : (unitPrice as number)} />
              {showTotalPriceSuffix ? <>~</> : null}
            </span>
          </div>
          {((isNotEmptyArray(price.salesPromotions) && basePrice && basePrice !== totalPrice) ||
            (isNotEmptyArray(price.salesPromotions) &&
              priceWithoutDiscount &&
              priceWithoutDiscount !== unitPrice)) && (
            <span
              className={cx(styles.baseAmount, isRowDiscountRateTag ? styles.rowTag : null)}
              data-testid="priceLabel-basePrice"
            >
              <div className={styles.basePrice}>
                <PriceComponent
                  value={isDirectContract ? basePrice : (priceWithoutDiscount as number)}
                />
              </div>

              {price?.discountRate && price?.discountRate >= 2 ? (
                <Tag
                  type="filled"
                  color={'scarlet'}
                  className={styles.tag}
                  data-testid="priceLabel-discountRate-tag"
                >
                  <Translate
                    id="Traveler_Common.Promotion_Feature.Amount.Suffix.Off"
                    data={{
                      discount_amount: `${price?.discountRate}%`,
                    }}
                  />
                </Tag>
              ) : null}
            </span>
          )}
          {Boolean(props.chargeUnit) && !isMapView && (
            <div className={styles.label} data-testid="priceLabel-chargeUnit-label">
              {props.chargeUnit}
            </div>
          )}
          {props.taxesLabel && isConsumptionTaxIncluded && (
            <div className={styles.label}>{props.taxesLabel}</div>
          )}
          {isDirectContract && noOfItemsPerGuest()}
          {props.isShowPlanPaymentRestriction && props.planPaymentRestrictionFeature && (
            <PlanPaymentRestriction
              className={cx(styles.label, styles.planPayment)}
              isShowIcon={false}
              {...props.planPaymentRestrictionFeature}
            />
          )}
        </div>
      </div>
      {/* total price section */}
      {isDisplayTotalPrice && !isDirectContract && (
        <div className={styles.totalPriceWrapper} data-testid="priceLabel-totalPrice">
          <span className={styles.totalPriceContainer}>
            <Translate
              id="Provider_List.Out_To_Out.Search_Results.Total"
              className={styles.total}
            />
            <div className={styles.total}>
              <PriceComponent value={totalPrice} />
            </div>
          </span>
          {noOfItemsPerGuest()}
        </div>
      )}
    </>
  );
}

export default PriceLabel;
